import { useState } from 'react'
import { useContent } from '../../contexts/ContentContext'
import SectionWrapper from '../ui/SectionWrapper'
import SectionLabel from '../ui/SectionLabel'
import LazyImage from '../ui/LazyImage'
import ExpandToggleButton from '../ui/ExpandToggleButton'
import AccordionWrap from '../ui/AccordionWrap'
import AnimatedAspectRatio from '../ui/AnimatedAspectRatio'
import { useAccordionScroll } from '../../hooks/useAccordionScroll'

export default function ResearchersSection() {
  const { researchers, ui } = useContent()
  const authors = researchers.authors
  const featuredAuthor = authors.find(author => author.featured)
  const [expandedId, setExpandedId] = useState(null)
  const toggleExpand = useAccordionScroll(expandedId, setExpandedId)

  // Resolve portrait object-position:
  // - portraitFocal: { x, y } (percentages, 0–100) — preferred, works across any aspect ratio
  // - portraitFocus: preset string — legacy fallback
  const resolvePosition = (focal, focus) => {
    if (focal && typeof focal.x === 'number' && typeof focal.y === 'number') {
      return `${focal.x}% ${focal.y}%`
    }
    const focusMap = {
      center: 'center',
      top: 'center top',
      bottom: 'center bottom',
      left: 'left center',
      right: 'right center',
      'top-left': 'left top',
      'top-right': 'right top',
      'bottom-left': 'left bottom',
      'bottom-right': 'right bottom',
    }
    return focusMap[focus] || 'center'
  }

  return (
    <SectionWrapper
      id="science"
      className="bg-color-primary text-primary-on-dark relative overflow-hidden"
      data-testid="science-section"
    >
      {/* Subtle paper texture overlay */}
      <div className="absolute inset-0 bg-paper opacity-20 pointer-events-none" />

      <div className="relative z-10">
        {/* Header */}
        <div className="max-w-3xl section-block-spacing">
          <SectionLabel text={researchers.authorBigLabel} light={true} />
          <h2 className="typography-h2-mobile-on-dark md:typography-h2-on-dark leading-tight">
            {researchers.authorHeadline}
          </h2>
        </div>

        {/* Featured Researcher */}
        {featuredAuthor && (
          <div className="flex flex-col sm:grid sm:grid-cols-[200px_1fr] md:grid-cols-[240px_1fr] lg:grid-cols-[255px_1fr] sm:card-grid-gap-lg margin-top-sm section-block-spacing">
            {/* Mobile: Photo first (full width, 16:9) */}
            <div className="md:hidden content-spacing">
              <AnimatedAspectRatio ratio={16 / 9}>
                <LazyImage
                  src={featuredAuthor.portrait}
                  alt={featuredAuthor.name}
                  className="w-full h-full object-cover rounded-lg"
                  objectPosition={resolvePosition(featuredAuthor.portraitFocal, featuredAuthor.portraitFocus)}
                />
              </AnimatedAspectRatio>
            </div>

            {/* Desktop: Photo - Left column */}
            <div className="hidden md:block w-full sm:max-w-[200px] md:max-w-[240px] lg:max-w-[255px]">
              <AnimatedAspectRatio ratio={1 / 1}>
                <LazyImage
                  src={featuredAuthor.portrait}
                  alt={featuredAuthor.name}
                  className="w-full h-full object-cover rounded-lg"
                  objectPosition={resolvePosition(featuredAuthor.portraitFocal, featuredAuthor.portraitFocus)}
                />
              </AnimatedAspectRatio>
            </div>

            {/* Right column: Quote + Name + Title */}
            <div className="flex flex-col text-on-dark">
              {/* Quote - Top of right column */}
              <blockquote className="typography-quote-featured-on-dark">
                "{featuredAuthor.featuredQuote}"
              </blockquote>

              {/* Name + Title - Below quote */}
              <div className="mt-8 -mt-2">
                <div className="typography-author-name-on-dark">{featuredAuthor.name}</div>
                <div className="typography-label-on-dark mt-1">{featuredAuthor.role}</div>
                <div className="typography-meta-on-dark mt-1">{featuredAuthor.lifeDates}</div>
              </div>
            </div>

            {/* Divider - Full width, more spacing below */}
            <div className="md:col-span-2 divider-spacing">
              <div className="h-px bg-color-border-light"></div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* ACCORDION CARDS SECTION — Phase 2: All Authors Dynamic */}
        {/* ═══════════════════════════════════════════════════════════ */}

        {authors.map((author, index) => {
          // Accordion cards can use a distinct portrait (e.g., Brian appears twice on the page)
          const cardPortrait = author.portraitAccordion || author.portrait
          const cardFocal = author.portraitFocalAccordion || author.portraitFocal
          return (
          <div key={author.id} className={index === authors.length - 1 ? 'mb-10' : 'mb-16'} data-testid={`researcher-${author.id}`}>
            {/* Row 1: Portrait + Name/Title/Year/ShortText + Button */}
            <div className="flex flex-col sm:grid sm:grid-cols-[200px_1fr] md:grid-cols-[240px_1fr] lg:grid-cols-[255px_1fr] sm:card-grid-gap-lg md:items-start">
              {/* Mobile: Portrait first (full width, 16:9) */}
              {cardPortrait && (
                <div className="md:hidden content-spacing">
                  <AnimatedAspectRatio ratio={16 / 9}>
                    <LazyImage
                      src={cardPortrait}
                      alt={author.name}
                      className="w-full h-full object-cover rounded-lg"
                      objectPosition={resolvePosition(cardFocal, author.portraitFocus)}
                    />
                  </AnimatedAspectRatio>
                </div>
              )}

              {/* Desktop: Portrait - Left column */}
              {cardPortrait && (
                <div className="hidden md:block w-full sm:max-w-[200px] md:max-w-[240px] lg:max-w-[255px] transition-all duration-[500ms] ease-in-out">
                  <AnimatedAspectRatio ratio={expandedId === author.id ? 3 / 4 : 1 / 1} duration="500ms">
                    <LazyImage
                      src={cardPortrait}
                      alt={author.name}
                      className="w-full h-full object-cover rounded-lg"
                      objectPosition={resolvePosition(cardFocal, author.portraitFocus)}
                    />
                  </AnimatedAspectRatio>
                </div>
              )}

              {/* Right column: Name/Title/Year/ShortText + Button */}
              <div className="flex flex-col min-w-0">
                {/* Mobile: Name/Role/Date above portrait */}
                <div className="md:hidden content-spacing-md">
                  <h3 className="typography-author-name-on-dark">
                    {author.name}
                  </h3>
                  <p className="typography-label-on-dark mt-1">
                    {author.role}
                  </p>
                  <p className="typography-meta-on-dark mt-1">{author.lifeDates}</p>
                </div>

                {/* Desktop: Name/Title/Year — negative top margin pulls the
                    name block up to align tightly with the portrait's top edge */}
                <div className="hidden md:block -mt-6">
                  <h3 className="typography-author-name-on-dark">
                    {author.name}
                  </h3>
                  <p className="typography-label-on-dark mt-1">
                    {author.role}
                  </p>
                  <p className="typography-meta-on-dark mt-1">{author.lifeDates}</p>
                </div>

                {/* Short text */}
                <p className="typography-summary-large-on-dark md:mt-4 content-spacing-md">
                  {author.shortVersion}
                </p>

                {/* Read more button - centered with divider lines */}
                <ExpandToggleButton
                  isExpanded={expandedId === author.id}
                  onToggle={(e) => toggleExpand(author.id, e)}
                  labelMore={ui.showMore}
                  labelLess={ui.showLess}
                  className="mt-6"
                />
              </div>
            </div>

            {/* Row 2: Expanded content (full width) with smooth animation */}
            <AccordionWrap isOpen={expandedId === author.id}>
              <div className="md:col-span-2">
                <div className="expanded-content-spacing">
                  <p className="typography-body-narrative-on-dark leading-relaxed content-spacing whitespace-pre-line">
                    {author.longVersion}
                  </p>
                  {author.sourceUrl && (
                    <a
                      href={author.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="typography-meta-on-dark hover:text-primary-on-dark transition-colors"
                    >
                      {author.sourceLabel} →
                    </a>
                  )}
                </div>
                {/* Divider - Full width, less spacing before */}
                <div className="md:col-span-2 pt-3">
                  <div className="h-px bg-color-border-light"></div>
                </div>
              </div>
            </AccordionWrap>
          </div>
          )
        })}
      </div>
    </SectionWrapper>
  )
}
